import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type {
  EditorSDK,
  RouterConfig,
} from '@wix/wix-data-client-common-standalone'
import { entries, nonNullable } from '@wix/wix-data-client-common-standalone'
import { isDynamicRoute } from '../../business-logic/dynamic-pages/isDynamicRoute'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type { AppApi } from '@wix/wix-data-client-common'

interface Params {
  collectionsApi: CollectionsApi
  editorSdkProxy: EditorSDK
  appApi: AppApi
  collectionId: string
  pageLinkKeys: string[]
}

const getItemPageRefs = async ({
  collectionsApi,
  editorSdkProxy,
  collectionId,
  pageLinkKeys,
}: Params): Promise<PageRef[]> => {
  const schema = await collectionsApi.get(collectionId)
  const itemPageLinkPatterns = pageLinkKeys
    .map(key => schema.fields[key])
    .filter(field => field && !field.isDeleted)
    .map(field => field.calculator?.config.pattern)
    .filter(nonNullable)
    .filter(route => isDynamicRoute({ route }))

  const slugsByPrefix = itemPageLinkPatterns.reduce(
    (acc, pattern) => {
      const [_, prefix, slug] = pattern.match(/^\/([^/]+)(\/.+)$/) || []
      if (prefix && slug) {
        acc[prefix] ??= []
        acc[prefix].push(slug)
      }
      return acc
    },
    {} as Record<string, string[]>,
  )

  const pageRefs = (
    await Promise.all(
      entries(slugsByPrefix).map(async ([prefix, slugs]) => {
        const routerRef = await editorSdkProxy.routers.getByPrefix({ prefix })
        if (!routerRef) {
          return []
        }
        const router = await editorSdkProxy.routers.get<RouterConfig>({
          routerRef,
        })

        const pageRefs = slugs
          .map(slug => router.config.patterns[slug])
          .filter(pattern => pattern?.config?.collection === collectionId)
          .map(
            ({ pageRole }) =>
              router.pages.find(page => page.pageRoles.includes(pageRole))
                ?.pageRef,
          )
          .filter(nonNullable)

        return pageRefs
      }),
    )
  ).flat()

  return pageRefs
}

export const refreshRoutersAndEmitPageLinkGenerationEvent = async ({
  collectionsApi,
  editorSdkProxy,
  appApi,
  collectionId,
  pageLinkKeys,
}: Params): Promise<void> => {
  const itemPageRefs = await getItemPageRefs({
    collectionsApi,
    editorSdkProxy,
    appApi,
    collectionId,
    pageLinkKeys,
  })

  if (itemPageRefs.length > 0) {
    editorSdkProxy.editor.routers.refresh()
    appApi.emit('itemPageLinksGenerated', itemPageRefs)
  }
}
